/**
 * Phase 24.4a — EmptyState primitive.
 *
 * Two variants:
 *   - inline: small taupe text. Drop-in replacement for existing
 *     <p className="text-[13px] text-taupe">{...}</p> convention.
 *   - hero: icon + heading + description + CTA slot. Used for prominent
 *     "no data yet, click here" surfaces (meal-plan empty state, etc.).
 *
 * Usage (inline):
 *   <EmptyState variant="inline" title="No data yet" />
 *
 * Usage (hero):
 *   <EmptyState
 *     variant="hero"
 *     icon={Utensils}
 *     title="No plan for this week"
 *     description="Generate one to see your meals."
 *   >
 *     <Button onClick={...}>Generate</Button>
 *   </EmptyState>
 */
import type { ReactNode } from "react";
import type { LucideIcon } from "lucide-react";
import { cn } from "@/lib/utils";

interface Props {
  variant: "inline" | "hero";
  title: string;
  description?: string;
  icon?: LucideIcon;
  children?: ReactNode;
  className?: string;
}

export function EmptyState({
  variant,
  title,
  description,
  icon: Icon,
  children,
  className,
}: Props) {
  if (variant === "inline") {
    return (
      <p
        className={cn("text-[13px] text-taupe", className)}
        data-testid="empty-state-inline"
      >
        {title}
        {description ? ` — ${description}` : null}
      </p>
    );
  }

  // hero variant
  return (
    <div
      className={cn(
        "flex flex-col items-center text-center py-12 px-6 gap-3",
        className,
      )}
      data-testid="empty-state-hero"
    >
      {Icon ? (
        <div className="rounded-full bg-cream-border/60 p-4">
          <Icon className="h-8 w-8 text-taupe" strokeWidth={1.5} aria-hidden="true" />
        </div>
      ) : null}
      <h3 className="font-serif text-[18px] font-medium text-navy-deep">{title}</h3>
      {description ? (
        <p className="max-w-md text-[14px] text-taupe">{description}</p>
      ) : null}
      {children ? <div className="mt-2">{children}</div> : null}
    </div>
  );
}
